import React, { useState, useEffect, useMemo } from 'react'
import { Layout } from '../../components/Layout/Layout'
import { Modal } from '../../components/Modal/Modal'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { fmtCurEUR } from '../../design/charts'
import { TRANSACTIONS_DEMO } from '../../design/demoData'
import { useToast } from '../../design/useToast'
import { Toast } from '../../design/Toast'
import { T, useContent } from '../../content/ContentProvider'

// Map a backend OrderTransaction to the design row shape.
function mapApiToRow(t) {
  const priceObj = t.orderPrice || {}
  const amount = priceObj.amount ? priceObj.amount / Math.pow(10, priceObj.scale ?? 2) : 0
  return {
    id: t.transactionId || t.id || '?',
    transactionTime: t.transactionTime || null,
    date: t.transactionTime ? new Date(t.transactionTime).toLocaleString('en-GB') : '—',
    selling: t.sellerRics || (t.sellerName || '').slice(0, 4) || '—',
    buying: t.supplierRics || (t.supplierName || '').slice(0, 4) || '—',
    agreement: t.agreementId || '—',
    amount: amount,
    currency: priceObj.currency || 'EUR',
    share: '—',
    net: amount,
    status: (t.status || 'pending').toLowerCase(),
    train: t.service || '—',
    route: '',
  }
}

export function Transactions() {
  const { t } = useContent()
  const [apiRows, setApiRows] = useState([])
  const [companies, setCompanies] = useState([])
  const [agreements, setAgreements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [density, setDensity] = useState('default') // compact | default | comfortable
  const [selected, setSelected] = useState(new Set())
  const [search, setSearch] = useState('')
  const [pageSize, setPageSize] = useState(50)
  const [page, setPage] = useState(0)
  const [detail, setDetail] = useState(null) // currently-viewed full transaction
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({
    transactionType: 'BOOKING', agreementId: '', seller: '', supplier: '',
    sellerOrderId: '', supplierOrderId: '', priceAmount: 12500, priceCurrency: 'EUR',
    priceType: 'CUSTOMER_PRICE', service: '',
  })
  const [creating, setCreating] = useState(false)
  const { toast, showToast } = useToast()
  const [dateRange, setDateRange] = useState('all') // all | 7d | 30d | thisMonth | lastMonth
  const [agreementFilter, setAgreementFilter] = useState('all')
  const [sortKey, setSortKey] = useState('date')
  const [sortDir, setSortDir] = useState('desc')
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(k); setSortDir('asc') }
  }
  const sortArrow = (k) => sortKey !== k ? '' : (sortDir === 'asc' ? '↑' : '↓')

  const reload = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/admin/partnerships?size=100').catch(() => ({ data: [] })),
      api.get('/api/admin/transactions?size=200').catch(() => ({ data: { content: [] } })),
      api.get('/api/admin/companies?size=100').catch(() => ({ data: [] })),
    ])
      .then(([agreementsRes, txRes, companiesRes]) => {
        const agrList = Array.isArray(agreementsRes.data) ? agreementsRes.data : agreementsRes.data.content || []
        setAgreements(agrList)
        const txs = Array.isArray(txRes.data) ? txRes.data : txRes.data.content || []
        setApiRows(txs)
        const cos = Array.isArray(companiesRes.data) ? companiesRes.data : companiesRes.data.content || []
        setCompanies(cos)
        setLoading(false)
      })
      .catch((err) => {
        setError(err.response?.data?.description || err.response?.data?.message || t('transactions.error.load'))
        setLoading(false)
      })
  }
  useEffect(() => { reload() }, [])

  // ── Actions ─────────────────────────────────────────────────────────────
  const exportCsv = async () => {
    try {
      const res = await api.get('/api/admin/transactions/export/csv', { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
      showToast('ok', t('transactions.toast.csvExported'))
    } catch (err) {
      showToast('err', err.response?.data?.description || t('transactions.toast.csvExportFailed'))
    }
  }

  const generateReports = async () => {
    if (!confirm(t('transactions.confirm.generateReports'))) return
    try {
      const res = await api.post('/api/admin/reports/generate')
      const d = res.data
      showToast('ok', t('transactions.toast.reportsGenerated', { created: d.reportsCreated, skipped: d.reportsSkipped }))
    } catch (err) {
      showToast('err', err.response?.data?.description || t('transactions.toast.reportGenerationFailed'))
    }
  }

  const fetchDetail = async (id) => {
    try {
      const res = await api.get(`/api/admin/transactions/${id}`)
      setDetail(res.data)
    } catch (err) {
      showToast('err', t('transactions.toast.detailFailed'))
    }
  }

  const createTx = async (e) => {
    e.preventDefault()
    setCreating(true)
    try {
      await api.post('/api/ordertransactions', {
        transactionType: createForm.transactionType,
        agreementId: createForm.agreementId,
        seller: createForm.seller,
        supplier: createForm.supplier,
        sellerOrderId: createForm.sellerOrderId || `SO-UI-${Date.now()}`,
        supplierOrderId: createForm.supplierOrderId || `SU-UI-${Date.now()}`,
        transactionTime: new Date().toISOString(),
        service: createForm.service || null,
        orderPrice: {
          currency: createForm.priceCurrency,
          amount: parseInt(createForm.priceAmount, 10),
          scale: 2,
        },
        priceType: createForm.priceType,
      }, { headers: { 'Idempotency-Key': crypto.randomUUID() } })
      showToast('ok', t('transactions.toast.created'))
      setShowCreate(false)
      reload()
    } catch (err) {
      showToast('err', err.response?.data?.description || t('transactions.toast.createFailed'))
    } finally {
      setCreating(false)
    }
  }

  // Determine if we're showing real data or demo
  const usingDemo = apiRows.length === 0 && !loading
  const sourceRows = useMemo(() => (usingDemo ? TRANSACTIONS_DEMO : apiRows.map(mapApiToRow)), [usingDemo, apiRows])

  // Apply status + search + date + agreement filters, then sort
  // Full filtered set (before pagination)
  const filtered = useMemo(() => {
    let r = sourceRows
    if (statusFilter !== 'all') r = r.filter((t) => t.status === statusFilter)
    if (agreementFilter !== 'all') r = r.filter((t) => t.agreement === agreementFilter)
    if (dateRange !== 'all') {
      const now = new Date()
      const cutoff = new Date(now)
      let from = null, to = null
      if (dateRange === '7d') { cutoff.setDate(now.getDate() - 7); from = cutoff; to = now }
      else if (dateRange === '30d') { cutoff.setDate(now.getDate() - 30); from = cutoff; to = now }
      else if (dateRange === 'thisMonth') {
        from = new Date(now.getFullYear(), now.getMonth(), 1)
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
      } else if (dateRange === 'lastMonth') {
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)
      }
      if (from && to) {
        r = r.filter((t) => {
          // t.date is a formatted "DD/MM/YYYY, HH:mm:ss" — fall back to original transactionTime
          const raw = t.transactionTime || t.dateRaw || t.date
          const d = raw ? new Date(raw) : null
          if (!d || isNaN(d)) return false
          return d >= from && d <= to
        })
      }
    }
    if (search.trim()) {
      const s = search.toLowerCase()
      r = r.filter(
        (t) =>
          t.id.toLowerCase().includes(s) ||
          t.agreement.toLowerCase().includes(s) ||
          (t.selling || '').toLowerCase().includes(s) ||
          (t.buying || '').toLowerCase().includes(s) ||
          (t.train || '').toLowerCase().includes(s)
      )
    }
    // Sort
    const dir = sortDir === 'asc' ? 1 : -1
    const sorted = [...r].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey]
      if (sortKey === 'date') { av = a.transactionTime || a.date; bv = b.transactionTime || b.date }
      if (sortKey === 'amount' || sortKey === 'net') { av = a[sortKey] || 0; bv = b[sortKey] || 0; return (av - bv) * dir }
      if (av == null) return 1
      if (bv == null) return -1
      return String(av).localeCompare(String(bv)) * dir
    })
    return sorted
  }, [sourceRows, statusFilter, agreementFilter, dateRange, search, sortKey, sortDir])

  // Reset page when filter/search/pageSize changes
  useEffect(() => { setPage(0) }, [statusFilter, agreementFilter, dateRange, search, pageSize, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages - 1)
  const pageStart = currentPage * pageSize
  const pageEnd = Math.min(pageStart + pageSize, filtered.length)
  const rows = useMemo(() => filtered.slice(pageStart, pageEnd), [filtered, pageStart, pageEnd])

  const counts = useMemo(
    () => ({
      all: sourceRows.length,
      reconciled: sourceRows.filter((t) => t.status === 'reconciled').length,
      pending: sourceRows.filter((t) => t.status === 'pending').length,
      disputed: sourceRows.filter((t) => t.status === 'disputed').length,
    }),
    [sourceRows]
  )

  const toggleSelect = (id) => {
    const next = new Set(selected)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelected(next)
  }

  return (
    <Layout crumb={t('transactions.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h"><T k="transactions.title" /></h1>
              <div className="page-head__sub">
                {usingDemo ? (
                  <T k="transactions.sub.demo" vars={{ agreements: agreements.length }} />
                ) : (
                  <T
                    k="transactions.sub.live"
                    vars={{ transactions: apiRows.length.toLocaleString('en-US'), agreements: agreements.length }}
                  />
                )}
              </div>
            </div>
            <div className="page-head__actions">
              <button className="btn" onClick={exportCsv}>{Icon.download({ s: 12 })} <T k="transactions.action.exportCsv" /></button>
              <button className="btn" onClick={generateReports}>{Icon.book({ s: 12 })} <T k="transactions.action.generateReports" /></button>
              <button className="btn btn--primary" onClick={() => setShowCreate(true)}>{Icon.upload({ s: 12 })} <T k="transactions.action.newTransaction" /></button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <div className="card">
          <div className="filter-bar">
            <div className="search-input">
              {Icon.search({ s: 12 })}
              <input
                placeholder={t('transactions.filter.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <label className="filter-chip" style={{ cursor: 'pointer' }}>
              <b><T k="transactions.filter.dateLabel" /></b>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                style={{ border: 'none', background: 'transparent', font: 'inherit', color: 'inherit', cursor: 'pointer', outline: 'none', padding: '0 4px' }}
              >
                <option value="all">{t('transactions.filter.date.all')}</option>
                <option value="7d">{t('transactions.filter.date.last7Days')}</option>
                <option value="30d">{t('transactions.filter.date.last30Days')}</option>
                <option value="thisMonth">{t('transactions.filter.date.thisMonth')}</option>
                <option value="lastMonth">{t('transactions.filter.date.lastMonth')}</option>
              </select>
              {Icon.chevronDown({ s: 10 })}
            </label>
            <label className="filter-chip" style={{ cursor: 'pointer' }}>
              <b><T k="transactions.filter.agreementLabel" /></b>
              <select
                value={agreementFilter}
                onChange={(e) => setAgreementFilter(e.target.value)}
                style={{ border: 'none', background: 'transparent', font: 'inherit', color: 'inherit', cursor: 'pointer', outline: 'none', padding: '0 4px', maxWidth: 200 }}
              >
                <option value="all">{t('transactions.filter.agreementAll', { count: agreements.length })}</option>
                {agreements.map((a) => (
                  <option key={a.id || a.agreementId} value={a.agreementId}>{a.agreementId}</option>
                ))}
              </select>
              {Icon.chevronDown({ s: 10 })}
            </label>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              {selected.size > 0 ? t('transactions.filter.selected', { count: selected.size }) : ''}
              <T k="transactions.filter.matching" vars={{ count: filtered.length.toLocaleString('en-US') }} />
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              {['compact', 'default', 'comfortable'].map((d) => (
                <button
                  key={d}
                  className={'filter-chip' + (density === d ? ' filter-chip--active' : '')}
                  onClick={() => setDensity(d)}
                  title={t('transactions.density.title', { density: d })}
                  style={{ textTransform: 'capitalize', fontSize: 11 }}
                >
                  {d[0].toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="tab-bar" style={{ marginTop: -1 }}>
            {[
              ['all', t('transactions.tab.all'), counts.all],
              ['reconciled', t('transactions.tab.reconciled'), counts.reconciled],
              ['pending', t('transactions.tab.pending'), counts.pending],
              ['disputed', t('transactions.tab.disputed'), counts.disputed],
            ].map(([k, l, c]) => (
              <button
                key={k}
                className={'tab-bar__tab ' + (statusFilter === k ? 'is-active' : '')}
                onClick={() => setStatusFilter(k)}
              >
                {l} <span className="count">{c}</span>
              </button>
            ))}
          </div>

          <div className="tbl-wrap">
            <table className={'tbl table--cards ' + (density === 'compact' ? 'tbl--compact' : density === 'comfortable' ? 'tbl--comfortable' : '')}>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input type="checkbox" />
                  </th>
                  {[
                    ['id', t('transactions.col.id')],
                    ['date', t('transactions.col.date')],
                    ['selling', t('transactions.col.selling')],
                    ['buying', t('transactions.col.buying')],
                    ['agreement', t('transactions.col.agreement')],
                    ['train', t('transactions.col.train')],
                  ].map(([k, label]) => (
                    <th key={k} onClick={() => toggleSort(k)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      <span className="th-sort">
                        {label} <span className="th-sort__arrow">{sortArrow(k)}</span>
                      </span>
                    </th>
                  ))}
                  <th className="num" onClick={() => toggleSort('amount')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <span className="th-sort"><T k="transactions.col.amount" /> <span className="th-sort__arrow">{sortArrow('amount')}</span></span>
                  </th>
                  <th><T k="transactions.col.split" /></th>
                  <th className="num" onClick={() => toggleSort('net')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <span className="th-sort"><T k="transactions.col.net" /> <span className="th-sort__arrow">{sortArrow('net')}</span></span>
                  </th>
                  <th onClick={() => toggleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <span className="th-sort"><T k="transactions.col.status" /> <span className="th-sort__arrow">{sortArrow('status')}</span></span>
                  </th>
                  <th style={{ width: 24 }}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-dim)' }}>
                      <T k="transactions.table.loading" />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-dim)' }}>
                      <T k="transactions.table.empty" />
                    </td>
                  </tr>
                ) : (
                  rows.map((tx) => {
                    const isSel = selected.has(tx.id)
                    return (
                      <tr
                        key={tx.id}
                        className={isSel ? 'is-selected' : ''}
                        onClick={() => !usingDemo && tx.id && fetchDetail(tx.id)}
                        style={{ cursor: !usingDemo && tx.id ? 'pointer' : 'default' }}
                      >
                        <td data-label={t('transactions.col.select')} onClick={(e) => { e.stopPropagation(); toggleSelect(tx.id) }}>
                          <input type="checkbox" checked={isSel} readOnly />
                        </td>
                        <td className="id bold" data-label={t('transactions.col.id')}>{tx.id}</td>
                        <td className="muted" data-label={t('transactions.col.date')}>{tx.date}</td>
                        <td data-label={t('transactions.col.selling')}>
                          <span className="rics">{tx.selling}</span>
                        </td>
                        <td data-label={t('transactions.col.buying')}>
                          <span className="rics">{tx.buying}</span>
                        </td>
                        <td className="id" data-label={t('transactions.col.agreement')}>{tx.agreement}</td>
                        <td data-label={t('transactions.col.train')}>
                          <span className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>
                            {tx.train}
                          </span>
                          {tx.route && (
                            <span
                              style={{
                                color: 'var(--text-dim)',
                                fontSize: 11,
                                marginLeft: 6,
                                fontFamily: 'var(--font-mono)',
                              }}
                            >
                              {tx.route}
                            </span>
                          )}
                        </td>
                        <td className="num bold" data-label={t('transactions.col.amount')}>{fmtCurEUR(tx.amount)}</td>
                        <td className="muted mono" data-label={t('transactions.col.split')} style={{ fontSize: 11 }}>
                          {tx.share}
                        </td>
                        <td className="num" data-label={t('transactions.col.net')}>{fmtCurEUR(tx.net)}</td>
                        <td data-label={t('transactions.col.status')}>
                          <span
                            className={
                              'pill pill--' +
                              ({ reconciled: 'ok', pending: 'warn', disputed: 'err' }[tx.status] || 'muted')
                            }
                          >
                            <span className="pill-dot" />
                            {tx.status}
                          </span>
                        </td>
                        <td data-label={t('transactions.col.actions')} onClick={(e) => e.stopPropagation()}>
                          <button
                            className="iconbtn"
                            style={{ width: 24, height: 24 }}
                            onClick={() => !usingDemo && tx.id && fetchDetail(tx.id)}
                            title={t('transactions.row.openDetails')}
                          >
                            {Icon.more({ s: 13 })}
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="tbl-foot">
            <span>
              <T k="transactions.foot.showing.a" />{' '}
              <b style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                {filtered.length === 0 ? 0 : pageStart + 1}–{pageEnd}
              </b>{' '}
              <T k="transactions.foot.showing.b" />{' '}
              <b style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                {filtered.length.toLocaleString('en-US')}
              </b>
            </span>
            <span style={{ marginLeft: 14, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <T k="transactions.foot.pageSize" />
              <select
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm, 4px)',
                  padding: '2px 6px',
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {[10, 25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </span>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dim)' }}>
              <T k="transactions.foot.pageOf" vars={{ page: currentPage + 1, total: totalPages }} />
            </span>
            <div className="pager">
              <button onClick={() => setPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0}>‹</button>
              <button className="is-active">{currentPage + 1}</button>
              <button onClick={() => setPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage >= totalPages - 1}>›</button>
            </div>
          </div>
        </div>
        </div>

        <Toast toast={toast} />

        {/* Transaction detail modal */}
        {detail && (
          <Modal isOpen={!!detail} onClose={() => setDetail(null)} title={t('transactions.detail.title', { id: detail.transactionId || detail.id })} size="lg">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                [t('transactions.detail.id'), detail.id],
                [t('transactions.detail.transactionId'), detail.transactionId],
                [t('transactions.detail.type'), detail.transactionType],
                [t('transactions.detail.agreement'), detail.agreementId],
                [t('transactions.detail.seller'), `${detail.sellerName || ''} (${detail.sellerRics || '—'})`],
                [t('transactions.detail.supplier'), `${detail.supplierName || ''} (${detail.supplierRics || '—'})`],
                [t('transactions.detail.sellerOrderId'), detail.sellerOrderId],
                [t('transactions.detail.supplierOrderId'), detail.supplierOrderId],
                [t('transactions.detail.transactionTime'), detail.transactionTime],
                [t('transactions.detail.price'), detail.orderPrice ? `${(detail.orderPrice.amount / Math.pow(10, detail.orderPrice.scale ?? 2)).toFixed(2)} ${detail.orderPrice.currency}` : '—'],
                [t('transactions.detail.priceType'), detail.priceType],
                [t('transactions.detail.service'), detail.service],
                [t('transactions.detail.idempotencyKey'), detail.idempotencyKey],
                [t('transactions.detail.createdAt'), detail.createdAt],
                // Keyed by position, not by label: the labels are editable now,
                // and two of them set to the same text would collide.
              ].map(([k, v], i) => (
                <div key={i} style={{ borderBottom: '1px solid var(--border-soft)', paddingBottom: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</div>
                  <div style={{ fontSize: 13, color: 'var(--text)', fontFamily: typeof v === 'string' && (v?.startsWith('urn:') || v?.startsWith('TX-') || /^[0-9a-f-]{36}$/.test(v || '')) ? 'var(--font-mono)' : 'var(--font-sans)', marginTop: 2, wordBreak: 'break-all' }}>
                    {v === null || v === undefined || v === '' ? <span style={{ color: 'var(--text-dim)' }}>—</span> : String(v)}
                  </div>
                </div>
              ))}
            </div>
          </Modal>
        )}

        {/* Create transaction modal */}
        {showCreate && (
          <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title={t('transactions.create.title')} size="md">
            <form onSubmit={createTx} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="fld">
                <label className="fld__label"><T k="transactions.create.type" /></label>
                <select className="fld__input" value={createForm.transactionType}
                  onChange={(e) => setCreateForm({ ...createForm, transactionType: e.target.value })}>
                  <option value="BOOKING">BOOKING</option>
                  <option value="CANCELATION">CANCELATION</option>
                </select>
              </div>
              <div className="fld">
                <label className="fld__label"><T k="transactions.create.agreement" /></label>
                <select className="fld__input" required value={createForm.agreementId}
                  onChange={(e) => setCreateForm({ ...createForm, agreementId: e.target.value })}>
                  <option value="">{t('transactions.create.agreementPlaceholder')}</option>
                  {agreements.map((a) => (
                    <option key={a.id} value={a.agreementId}>{a.name || a.agreementId}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="fld">
                  <label className="fld__label"><T k="transactions.create.seller" /></label>
                  <select className="fld__input" required value={createForm.seller}
                    onChange={(e) => setCreateForm({ ...createForm, seller: e.target.value })}>
                    <option value="">{t('transactions.create.sellerPlaceholder')}</option>
                    {companies.map((c) => (<option key={c.id} value={c.companyId}>{c.name} ({c.rics})</option>))}
                  </select>
                </div>
                <div className="fld">
                  <label className="fld__label"><T k="transactions.create.supplier" /></label>
                  <select className="fld__input" required value={createForm.supplier}
                    onChange={(e) => setCreateForm({ ...createForm, supplier: e.target.value })}>
                    <option value="">{t('transactions.create.supplierPlaceholder')}</option>
                    {companies.map((c) => (<option key={c.id} value={c.companyId}>{c.name} ({c.rics})</option>))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                <div className="fld">
                  <label className="fld__label"><T k="transactions.create.price" /></label>
                  <input type="number" className="fld__input" required min="1"
                    value={createForm.priceAmount}
                    onChange={(e) => setCreateForm({ ...createForm, priceAmount: e.target.value })} />
                </div>
                <div className="fld">
                  <label className="fld__label"><T k="transactions.create.currency" /></label>
                  <select className="fld__input" value={createForm.priceCurrency}
                    onChange={(e) => setCreateForm({ ...createForm, priceCurrency: e.target.value })}>
                    <option>EUR</option><option>CHF</option><option>PLN</option><option>GBP</option>
                  </select>
                </div>
                <div className="fld">
                  <label className="fld__label"><T k="transactions.create.priceType" /></label>
                  <select className="fld__input" value={createForm.priceType}
                    onChange={(e) => setCreateForm({ ...createForm, priceType: e.target.value })}>
                    <option value="CUSTOMER_PRICE">CUSTOMER_PRICE</option>
                    <option value="SETTLEMENT_VALUE">SETTLEMENT_VALUE</option>
                  </select>
                </div>
              </div>
              <div className="fld">
                <label className="fld__label"><T k="transactions.create.service" /></label>
                <input type="text" className="fld__input" placeholder={t('transactions.create.servicePlaceholder')}
                  value={createForm.service}
                  onChange={(e) => setCreateForm({ ...createForm, service: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button type="button" className="btn" onClick={() => setShowCreate(false)}><T k="transactions.create.cancel" /></button>
                <button type="submit" className="btn btn--primary" disabled={creating}>
                  {creating ? t('transactions.create.submitBusy') : t('transactions.create.submit')}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </Layout>
  )
}
